import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Popover, Spin } from 'antd';
import type { ReportListItem, ReportListResult } from '../types';
import { parseReportUrl } from '../utils/parseReportUrl';
import { usePopupHost } from '../PopupHostContext';

export type ReportDropdownNodeType =
  | 'report-url'
  | 'multi-variable'
  | 'multi-variable-select';

export type ReportListFetcher = (params: {
  nodeKey: string;
  nodeType: ReportDropdownNodeType;
}) => Promise<ReportListResult>;

const EMPTY_IMG_URL =
  'https://sealseek-public-read.oss-cn-hangzhou.aliyuncs.com/file/website/kongzhuangtai.svg';

/** URL 输入态时的精确匹配 key（按 reportId 等价比较） */
export const reportMatchKey = (url: string): string => {
  if (!url) return '';
  const parsed = parseReportUrl(url);
  return parsed.reportId ? `report:${parsed.reportId}` : `url:${url.trim()}`;
};

/** dropdown 面板：tabs + 列表 + 空态 + 查看报告 + is-scrolling 动画 */
const DropdownPanel: React.FC<{
  nodeKey: string;
  nodeType: ReportDropdownNodeType;
  fetch: ReportListFetcher;
  onSelect: (item: ReportListItem) => void;
  onGoGenerate?: (agentType: string | number) => void;
  onViewReport?: (item: ReportListItem) => void;
  hideCurrentSessionReport?: boolean;
  preciseMatchKey: string;
  selectedReportKey: string;
  open: boolean;
}> = ({
  nodeKey,
  nodeType,
  fetch,
  onSelect,
  onGoGenerate,
  onViewReport,
  hideCurrentSessionReport,
  preciseMatchKey,
  selectedReportKey,
  open,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ReportListResult | null>(null);
  const [activeTabKey, setActiveTabKey] = useState<string>('');
  const seqRef = useRef(0);
  const scrollTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    const seq = ++seqRef.current;
    setLoading(true);
    fetch({ nodeKey, nodeType })
      .then((r) => {
        if (seq !== seqRef.current) return;
        const tabs = hideCurrentSessionReport
          ? r.tabs.filter((t) => t.key !== 'currentConversationReports')
          : r.tabs;
        const filtered: ReportListResult = { ...r, tabs };
        setData(filtered);
        const firstNonEmpty = tabs.find((t) => t.items.length > 0);
        setActiveTabKey(firstNonEmpty?.key ?? tabs[0]?.key ?? '');
      })
      .catch(() => {
        if (seq !== seqRef.current) return;
        setData({ tabs: [] });
      })
      .finally(() => {
        if (seq !== seqRef.current) return;
        setLoading(false);
      });
  }, [open, fetch, nodeKey, nodeType, hideCurrentSessionReport]);

  const filterItems = useCallback(
    (items: ReportListItem[]) =>
      preciseMatchKey
        ? items.filter((i) => reportMatchKey(i.url) === preciseMatchKey)
        : items,
    [preciseMatchKey],
  );

  // precise 模式：定位到第一个含匹配 item 的 tab
  useEffect(() => {
    if (!open || !preciseMatchKey || !data) return;
    const matched = data.tabs.find((t) => filterItems(t.items).length > 0);
    if (matched) setActiveTabKey((p) => (p === matched.key ? p : matched.key));
  }, [open, preciseMatchKey, data, filterItems]);

  const activeTab = data?.tabs.find((t) => t.key === activeTabKey);
  const items = activeTab ? filterItems(activeTab.items) : [];
  const hasTabs = (data?.tabs.length ?? 0) > 1;

  const handleListScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    el.classList.add('is-scrolling');
    if (scrollTimerRef.current) window.clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = window.setTimeout(() => {
      el.classList.remove('is-scrolling');
      scrollTimerRef.current = null;
    }, 300);
  }, []);

  const handleViewReport = useCallback(
    (e: React.MouseEvent, item: ReportListItem) => {
      e.stopPropagation();
      if (onViewReport) onViewReport(item);
      else window.open(item.viewUrl || item.url, '_blank');
    },
    [onViewReport],
  );

  return (
    <div
      className={`rte-report-dropdown-panel${
        hasTabs ? ' rte-report-dropdown-panel-with-tabs' : ''
      }`}
      onMouseDown={(e) => e.preventDefault()}
    >
      {loading ? (
        <div className="rte-report-dropdown-loading">
          <Spin size="small" />
        </div>
      ) : data ? (
        <>
          {hasTabs && (
            <div className="rte-report-dropdown-tab-outer">
              <div className="rte-report-dropdown-tab-inner">
                {data.tabs.map((t) => (
                  <div
                    key={t.key}
                    className={`rte-report-dropdown-tab-item${
                      t.key === activeTabKey ? ' active' : ''
                    }`}
                    onClick={() => setActiveTabKey(t.key)}
                  >
                    {t.label}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="rte-report-dropdown-list" onScroll={handleListScroll}>
            {!activeTab || items.length === 0 ? (
              <div className="rte-report-dropdown-empty">
                <img
                  className="rte-report-dropdown-empty-img"
                  src={EMPTY_IMG_URL}
                  alt=""
                />
                <div className="rte-report-dropdown-empty-text">
                  <span className="rte-report-dropdown-empty-label">
                    {preciseMatchKey ? '未找到匹配的报告' : '暂无生成的报告'}
                  </span>
                  {onGoGenerate && activeTab?.agentType !== undefined && (
                    <span
                      className="rte-report-dropdown-empty-link"
                      onClick={() => onGoGenerate(activeTab.agentType!)}
                    >
                      去生成
                    </span>
                  )}
                </div>
              </div>
            ) : (
              items.map((item, idx) => (
                <div
                  key={`${item.url}-${idx}`}
                  className={`rte-report-dropdown-item${
                    selectedReportKey && reportMatchKey(item.url) === selectedReportKey
                      ? ' rte-report-dropdown-item-selected'
                      : ''
                  }`}
                  onClick={() => onSelect(item)}
                >
                  <span className="rte-report-dropdown-item-name">
                    {item.name}
                  </span>
                  <span
                    className="rte-report-dropdown-item-action"
                    onClick={(e) => handleViewReport(e, item)}
                  >
                    <i className="iconfont icon-chakanbaogao" />
                    查看报告
                  </span>
                </div>
              ))
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

/**
 * 共享报告 dropdown：渲染 host icon + overlay portal + 自动锚定/翻转/互斥广播
 * 三类节点（report-url / multi-variable / multi-variable-select）共享这套实现
 */
export const ReportDropdown: React.FC<{
  /** 用于定位 overlay 锚点的 chip（或包裹元素）ref */
  chipRef: React.RefObject<HTMLElement | null>;
  nodeKey: string;
  nodeType: ReportDropdownNodeType;
  fetch: ReportListFetcher;
  onSelect: (item: ReportListItem) => void;
  onGoGenerate?: (agentType: string | number) => void;
  onViewReport?: (item: ReportListItem) => void;
  hideCurrentSessionReport?: boolean;
  preciseMatchKey?: string;
  selectedReportKey?: string;
  /** name chip 模式下加 -quiet 类（与 clear icon 共位 hover 互斥） */
  quiet?: boolean;
  /** 通过外部信号控制开关（如 chip 点击触发） */
  openSignal?: number;
}> = ({
  chipRef,
  nodeKey,
  nodeType,
  fetch,
  onSelect,
  onGoGenerate,
  onViewReport,
  hideCurrentSessionReport,
  preciseMatchKey = '',
  selectedReportKey = '',
  quiet,
  openSignal,
}) => {
  const popupHost = usePopupHost();
  const [open, setOpen] = useState(false);
  const hostRef = useRef<HTMLSpanElement>(null);
  const broadcastId = useRef({}).current;

  const [placement, setPlacement] = useState<'bottomLeft' | 'topLeft'>('bottomLeft');
  const [offsetX, setOffsetX] = useState(0);
  const dropdownOffsetY = nodeType === 'multi-variable-select' ? 11 : 4;

  const updatePlacement = useCallback(() => {
    const chip = chipRef.current;
    const host = hostRef.current;
    if (!chip || !host) return;
    const chipRect = chip.getBoundingClientRect();
    const iconRect = host.getBoundingClientRect();
    setOffsetX(chipRect.left - iconRect.left);
    const PANEL_H = 236;
    const spaceBelow = window.innerHeight - chipRect.bottom;
    setPlacement(spaceBelow < PANEL_H ? 'topLeft' : 'bottomLeft');
  }, [chipRef]);

  useLayoutEffect(() => {
    if (open) updatePlacement();
  }, [open, updatePlacement]);

  const popoverAlign = useMemo(
    () => ({
      offset: [
        offsetX,
        placement === 'bottomLeft' ? dropdownOffsetY : -dropdownOffsetY,
      ],
      overflow: { adjustX: false, adjustY: false },
    }),
    [dropdownOffsetY, offsetX, placement],
  );

  const openDropdown = useCallback(() => {
    document.dispatchEvent(
      new CustomEvent('rte-report-dropdown-opening', { detail: broadcastId }),
    );
    setOpen(true);
  }, [broadcastId]);

  // 外部触发（chip 点击）：openSignal 变化时打开
  useEffect(() => {
    if (openSignal === undefined) return;
    openDropdown();
  }, [openSignal, openDropdown]);

  // 互斥广播：别人开则自己关
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail !== broadcastId) setOpen(false);
    };
    document.addEventListener('rte-report-dropdown-opening', handler);
    return () => document.removeEventListener('rte-report-dropdown-opening', handler);
  }, [broadcastId]);

  // 点击外部关闭
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const chip = chipRef.current;
      const t = e.target as Node;
      if (chip?.contains(t)) return;
      const overlays = document.querySelectorAll('.rte-report-dropdown-overlay');
      for (let i = 0; i < overlays.length; i++) {
        if (overlays[i].contains(t)) return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, chipRef]);

  const handleSelect = useCallback(
    (item: ReportListItem) => {
      setOpen(false);
      onSelect(item);
    },
    [onSelect],
  );

  const dropdownContent = open ? (
    <DropdownPanel
      nodeKey={nodeKey}
      nodeType={nodeType}
      fetch={fetch}
      onSelect={handleSelect}
      onGoGenerate={onGoGenerate}
      onViewReport={onViewReport}
      hideCurrentSessionReport={hideCurrentSessionReport}
      preciseMatchKey={preciseMatchKey}
      selectedReportKey={selectedReportKey}
      open={open}
    />
  ) : null;

  return (
    <Popover
      content={dropdownContent}
      trigger={[]}
      open={open}
      placement={placement}
      align={popoverAlign}
      autoAdjustOverflow={false}
      getPopupContainer={() => popupHost ?? document.body}
      overlayClassName="rte-report-dropdown-overlay"
      arrow={false}
    >
      <span
        ref={hostRef}
        className={[
          'rte-report-dropdown-host',
          quiet ? 'rte-report-dropdown-host-quiet' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-key={nodeKey}
        data-node-type={nodeType}
        contentEditable={false}
        onClick={(e) => {
          e.stopPropagation();
          if (open) setOpen(false);
          else openDropdown();
        }}
      >
        <i
          className="iconfont icon-qiehuan-xiayizhang rte-report-dropdown-icon"
          aria-label="选择报告"
        />
      </span>
    </Popover>
  );
};
